import { Utils } from '@Util/utils'

import './styles.scss'
import * as Toast from '@radix-ui/react-toast'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'

const CLASS_NAME = 'gby-k2g-draw-info-panel'

export type ButtonControl = {
  onClick: () => void
  disabled: boolean
}

export type AnimationDrawInfoPanelProps = {
  drawNumber: number | null
  winsThisDraw: number | null
  winsTotal: number | null
  currentSelectedNumber: number | null
}

type IncrementalWinning = {
  visible: boolean
  amount: number
  cumulative: number
}

export const AnimationDrawInfoPanel = (props: AnimationDrawInfoPanelProps) => {
  const [incrementalWinnings, setIncrementalWinnings] = useState<IncrementalWinning[]>([])
  const hostConfig = useSelector((state: RootState) => state.hostConfig.config)

  useEffect(() => {
    let latestCumulative = 0
    if (incrementalWinnings.length > 0) {
      latestCumulative = incrementalWinnings[incrementalWinnings.length - 1].cumulative
    }

    if (props.winsThisDraw && props.winsThisDraw > latestCumulative) {
      setIncrementalWinnings([
        ...incrementalWinnings,
        {
          visible: true,
          cumulative: props.winsThisDraw,
          amount: props.winsThisDraw - latestCumulative,
        },
      ])
    } else if (
      props.winsThisDraw === 0 ||
      (props.winsThisDraw && props.winsThisDraw < latestCumulative)
    ) {
      setIncrementalWinnings([])
    }
  }, [props.winsThisDraw, incrementalWinnings])

  const updateToastAt = useCallback(
    (index: number, state: boolean) => {
      incrementalWinnings[index].visible = state
      setIncrementalWinnings([...incrementalWinnings])
    },
    [incrementalWinnings],
  )

  return (
    <div className={`${CLASS_NAME}`}>
      <Toast.Provider>
        {incrementalWinnings.map((w, i) => (
          <Toast.Root
            open={w.visible}
            key={i}
            onOpenChange={(open) => updateToastAt(i, open)}
            duration={3100}
            onSwipeEnd={(event) => event.preventDefault()}
            className={`${CLASS_NAME}__toast ${w.visible ? 'appear' : ''}`}>
            <Toast.Description>
              <div className={`${CLASS_NAME}__toast-message`}>
                <div className={`${CLASS_NAME}__toast-message-plus`}>+</div>
                <div>{Utils.convertCentsNumberToDollarString(w.amount)}</div>
              </div>
            </Toast.Description>
          </Toast.Root>
        ))}
        <Toast.Viewport className={`${CLASS_NAME}__toast-container`} />
      </Toast.Provider>

      <div className={`${CLASS_NAME}__container`}>
        <div
          className={`${CLASS_NAME}__text`}
          id={'anim-info-draw-number-text'}
          aria-live={'polite'}
          aria-labelledby={'anim-info-draw-number-label anim-info-draw-number-text'}>
          {props.drawNumber ? props.drawNumber : ' - '}
        </div>
        <div
          className={`${CLASS_NAME}__label`}
          id={'anim-info-draw-number-label'}
          aria-hidden={'true'}>
          {hostConfig?.animationDrawInfo.drawNumberLabel ?? 'Drawing'}
        </div>
      </div>
      <hr className={`${CLASS_NAME}__divider`} />

      <div className={`${CLASS_NAME}__container`}>
        {props.winsThisDraw !== null ? (
          <div
            className={`${CLASS_NAME}__text`}
            id={'anim-info-wins-this-draw-text'}
            aria-labelledby={'anim-info-wins-this-draw-label anim-info-wins-this-draw-text'}>
            {Utils.convertCentsNumberToDollarString(props.winsThisDraw)}
          </div>
        ) : null}
        {props.winsThisDraw !== null ? (
          <div
            className={`${CLASS_NAME}__label`}
            id={'anim-info-wins-this-draw-label'}
            aria-hidden={'true'}>
            {hostConfig?.animationDrawInfo.winsThisDrawLabel ?? 'Wins This Draw'}
          </div>
        ) : null}
      </div>
      <div className={`${CLASS_NAME}__container`}>
        {props.winsTotal !== null ? (
          <div
            className={`${CLASS_NAME}__text`}
            id={'anim-info-total-wins-text'}
            aria-labelledby={'anim-info-total-wins-label anim-info-total-wins-text'}>
            {Utils.convertCentsNumberToDollarString(props.winsTotal)}
          </div>
        ) : null}
        {props.winsTotal !== null ? (
          <div
            className={`${CLASS_NAME}__label`}
            id={'anim-info-total-wins-label'}
            aria-hidden={'true'}>
            Total Wins
          </div>
        ) : null}
      </div>
      <div className={`${CLASS_NAME}__sr-desc`} aria-live={'polite'}>
        {`#${props.currentSelectedNumber} selected. ${props.winsThisDraw ? `${Utils.convertCentsNumberToDollarString(props.winsThisDraw)} ${hostConfig?.animationDrawInfo.screenReaderWinsThisDrawText ?? 'this draw'}.` : ''} ${props.winsTotal ? `${Utils.convertCentsNumberToDollarString(props.winsTotal)} total.` : ''}`}
      </div>
    </div>
  )
}
